import { useQuery } from "@tanstack/react-query";
import { supabase } from "../lib/supabase";
import { ProcessStatus, ProcessStatusRecord, MeetingProcessStatus } from "../types/process";

export function useMeetingStatuses(userId: string | undefined) {
  return useQuery({
    queryKey: ['meeting-statuses', userId],
    queryFn: async () => {
      if (!userId) {
        throw new Error('User ID is required');
      }

      const { data: meetings, error: meetingsError } = await supabase
        .from('meetings')
        .select('id, title, created_at')
        .eq('user_id', userId)
        .order('created_at', { ascending: false });

      if (meetingsError) throw meetingsError;

      const { data: statuses, error: statusesError } = await supabase
        .from('process_statuses')
        .select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: false });

      if (statusesError) throw statusesError;

      const meetingStatuses: MeetingProcessStatus[] = meetings.map((meeting) => {
        const meetingProcesses = (statuses as ProcessStatusRecord[]).filter(
          (status) => status.meeting_id === meeting.id
        );

        const getLatestStatus = (processType: string): ProcessStatus => {
          const processStatuses = meetingProcesses
            .filter(s => s.process_type === processType)
            .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
          return processStatuses[0]?.status || 'queued';
        };

        const error_messages: Record<string, string> = {};
        ['upload', 'transcription', 'analysis'].forEach(type => {
          const latest = meetingProcesses
            .filter(s => s.process_type === type)
            .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())[0];
          if (latest?.status === 'error' && latest.error_message) {
            error_messages[type] = latest.error_message;
          }
        });

        return {
          meeting,
          statuses: {
            upload: getLatestStatus('upload'),
            transcription: getLatestStatus('transcription'),
            analysis: getLatestStatus('analysis'),
            quick_summary: getLatestStatus('quick_summary'),
            action_items: getLatestStatus('action_items'),
            detailed_notes: getLatestStatus('detailed_notes'),
          },
          error_messages: Object.keys(error_messages).length > 0 ? error_messages : undefined
        };
      });

      return meetingStatuses;
    },
    enabled: !!userId,
    refetchInterval: 3000, // Poll every 3 seconds for updates
  });
}